import { ResponseData } from "@api/internal/utils/types";
import { getUrlPathFromAbsoluteUrl } from "@routes/Article/utils/helpers";
import { sanitizeArticleData } from "@routes/Article/utils/sanitizeArticleData";
import { FeedArticleData, FeedArticleItems } from "@routes/Article/utils/types";
import { tryCatch } from "@typings/TryCatch";
import { getEnvironment } from "@util/constant";
import { getEnvironmentDomain } from "@util/helpers";
import axios from "axios";

type UseFetchArticleProps = {
  /** Urlpath of the article to fetch the article */
  urlPath?: string;
  lang?: string;
};

/**
 * Hook to fetch article data based on the provided URL path.
 */
export const useFetchArticle = async ({
  urlPath,
  lang = "en",
}: UseFetchArticleProps): Promise<FeedArticleData | undefined> => {
  const endpoint = "/_plat/api/v1/article";
  const params = { urlPath, lang };
  const request = axios.get<ResponseData<FeedArticleItems>>(endpoint, {
    params,
  });

  const { data, error } = await tryCatch(request);

  if (error) {
    // eslint-disable-next-line no-console
    console.error(
      "Error fetching next section articles:",
      JSON.stringify({ endpoint, params, error })
    );

    return;
  }

  const responseData = data.data;

  if (!responseData.success) {
    // eslint-disable-next-line no-console
    console.error(
      "Error fetching next section articles: Backend API returned an error",
      JSON.stringify({ endpoint, params, error })
    );

    return;
  }

  const articleContent = responseData.data;
  const longURL = articleContent.urlPath;

  // Transpose urlPath for chinese translation, if needed.
  const path = getUrlPathFromAbsoluteUrl(longURL);
  const env = getEnvironment();
  const payload = {
    ...articleContent,
    urlPath:
      lang === "zh-hans"
        ? `${getEnvironmentDomain(env)}/zh-hans${path}`
        : longURL,
  };

  const isSummaryAvailable = !!(
    articleContent.aiSummary && articleContent.aiSummary.trim().length > 0
  );

  const sanitizedArticleData = sanitizeArticleData(payload);

  return { ...sanitizedArticleData, isSummaryAvailable };
};
